import axios from 'axios';

import { API_HOST } from "../config";
import TokenHandler from "./base";

export interface TwilioSubaccount {
  sid: string;
  authToken: string;
  friendlyName: string;
  dateCreated: Date;
  status: string;
}

export interface TwilioApiKey {
  sid: string;
  friendlyName: string;
  secret: string;
  dateCreated: Date;
}

export interface TwilioPhoneNumber {
  sid: string;
  phoneNumber: string;
  friendlyName: string;
  capabilities: {
    sms: boolean;
    mms: boolean;
    voice: boolean;
  };
  dateCreated: Date;
}

export interface TwilioMessage {
  sid: string;
  from: string;
  to: string;
  body: string;
  status: string;
  direction: string;
  dateCreated: Date;
}

export interface TwilioConfig {
  accountSid: string;
  authToken: string;
  username: string;
  phoneNumber: string;
  subaccount: TwilioSubaccount;
  apiKeys: TwilioApiKey[];
  numbers: TwilioPhoneNumber[];
}

export interface AvailablePhoneNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string;
  region: string;
  capabilities: {
    sms: boolean;
    mms: boolean;
    voice: boolean;
  };
}

const twilioService = {
  // Subaccount endpoints
  createSubaccount: async (organizationId: string, friendlyName: string) => {
    const response = await axios.post(`${API_HOST}/twilio/subaccounts`, { friendlyName }, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  getSubaccount: async (organizationId: string) => {
    const response = await axios.get(`${API_HOST}/twilio/subaccounts`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  // API Keys endpoints
  createApiKey: async (organizationId: string, friendlyName: string) => {
    const response = await axios.post(`${API_HOST}/twilio/api-keys`, { friendlyName }, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  listApiKeys: async (organizationId: string) => {
    const response = await axios.get(`${API_HOST}/twilio/api-keys`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  deleteApiKey: async (organizationId: string, keySid: string) => {
    const response = await axios.delete(`${API_HOST}/twilio/api-keys/${keySid}`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  // Phone Numbers endpoints
  searchPhoneNumbers: async (organizationId: string, areaCode: string, country = 'US') => {
    const response = await axios.get(
      `${API_HOST}/twilio/available-numbers`,
      { params: { areaCode, country }, headers: TokenHandler.getHeaders() }
    );
    return response.data;
  },

  purchasePhoneNumber: async (phoneNumber: string, friendlyName: string) => {
    const response = await axios.post(
      `${API_HOST}/twilio/phone-numbers`,
      { phoneNumber, friendlyName },
      {
        headers: TokenHandler.getHeaders(),
      }
    );
    return response.data;
  },

  listPhoneNumbers: async () => {
    const response = await axios.get(`${API_HOST}/twilio/phone-numbers`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  releasePhoneNumber: async (numberSid: string) => {
    const response = await axios.delete(`${API_HOST}/twilio/phone-numbers/${numberSid}`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  // Messages endpoints
  listMessages: async (page = 1, limit = 10) => {
    const response = await axios.get(
      `${API_HOST}/twilio/messages`,
      { params: { page, limit }, headers: TokenHandler.getHeaders() }
    );
    return response.data;
  },

  sendMessage: async (to: string, body: string, from?: string) => {
    const response = await axios.post(
      `${API_HOST}/twilio/messages`,
      { to, body, from },
      {
        headers: TokenHandler.getHeaders(),
      }
    );
    return response.data;
  },

  // Twilio Configuration
  getTwilioConfig: async () => {
    const response = await axios.get(`${API_HOST}/twilio/config`, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  },

  updateTwilioConfig: async (config: Partial<TwilioConfig>) => {
    const response = await axios.patch(`${API_HOST}/twilio/config`, config, {
      headers: TokenHandler.getHeaders(),
    });
    return response.data;
  }
};

export default twilioService; 